import type { Product } from "@/hooks/useProducts";
import type { Project } from "@/hooks/useProjects";
import type { RunResultsPayload } from "@/lib/types";

const now = new Date().toISOString();

export const DEMO_USER = {
  id: "demo-user-reqqy",
  email: "demo@reqqy.local",
  app_metadata: {},
  user_metadata: { name: "Demo User" },
  aud: "authenticated",
  created_at: now,
} as any;

export const DEMO_PRODUCTS: Product[] = [
  {
    id: "reqqy",
    name: "Reqqy",
    description: "Requirements intelligence workspace for turning product ideas into reviewable, engineering-ready requirements.",
    color: null,
    owner_user_id: DEMO_USER.id,
    organization_id: null,
    created_at: now,
    updated_at: now,
    project_ids: ["demo-124", "rider-tracking"],
  },
  {
    id: "logistics-demo",
    name: "Logistics Aggregator",
    description: "Demo delivery operations product used to test existing-product feature workflows.",
    color: null,
    owner_user_id: DEMO_USER.id,
    organization_id: null,
    created_at: now,
    updated_at: now,
    project_ids: ["rider-tracking"],
  },
];

export const DEMO_PROJECTS: Project[] = [
  {
    id: "demo-124",
    organization_id: null,
    owner_user_id: DEMO_USER.id,
    name: "demo 124",
    description: "Feature workspace for demonstrating runs, reviews, refinement, and product memory.",
    pinned: true,
    color: null,
    tags: ["demo", "requirements"],
    created_at: now,
    updated_at: now,
    runs_count: 14,
    last_run_at: now,
    avg_clarity_score: 82,
    product_id: "reqqy",
    product_name: "Reqqy",
  },
  {
    id: "rider-tracking",
    organization_id: null,
    owner_user_id: DEMO_USER.id,
    name: "Live rider tracking",
    description: "Existing-product feature initiative for customer and merchant delivery visibility.",
    pinned: false,
    color: null,
    tags: ["logistics", "visibility"],
    created_at: now,
    updated_at: now,
    runs_count: 5,
    last_run_at: now,
    avg_clarity_score: 74,
    product_id: "logistics-demo",
    product_name: "Logistics Aggregator",
  },
  {
    id: "orphan-project",
    organization_id: null,
    owner_user_id: DEMO_USER.id,
    name: "Unassigned project",
    description: "A project that can be attached to a product later.",
    pinned: false,
    color: null,
    tags: ["unassigned"],
    created_at: now,
    updated_at: now,
    runs_count: 2,
    last_run_at: now,
    avg_clarity_score: undefined,
    product_id: null,
    product_name: null,
  },
];

export const DEMO_RUNS = [
  {
    id: "demo-run-1",
    name: "Generate requirements for product memory",
    project_id: "demo-124",
    module_type: "generation",
    status: "succeeded",
    clarity_score: 82,
    created_at: now,
    completed_at: now,
    provider: "demo",
    model: "reqqy-demo",
    processing_time_ms: 2600,
    tokens_used: 1850,
    credits_consumed: 1,
    input_payload: {
      feature_title: "Product memory and refinement loops",
      raw_idea: "Allow users to teach Reqqy product context and refine generated requirements through prompts.",
    },
  },
  {
    id: "demo-run-2",
    name: "Review live tracking requirements",
    project_id: "rider-tracking",
    module_type: "evaluation",
    status: "succeeded",
    clarity_score: 74,
    created_at: now,
    completed_at: now,
    provider: "demo",
    model: "reqqy-demo",
    processing_time_ms: 3100,
    tokens_used: 2400,
    credits_consumed: 2,
    input_payload: {
      feature_title: "Live rider tracking",
      raw_idea: "Customers should see rider progress after pickup.",
    },
  },
] as any[];

export const DEMO_REQUIREMENTS_DOCUMENT = {
  title: "Product Memory and Refinement Loop",
  summary: "A requirements workspace where product memory informs runs and users can refine all or selected requirements through prompts.",
  epics: [
    {
      title: "Product Memory",
      description: "Long-term context that Reqqy can retrieve during generation, review, and refinement.",
      stories: [
        {
          id: "US-1",
          role: "product manager",
          goal: "save product rules, workflows, and decisions as memory",
          benefit: "future requirements are generated against the actual product context",
          acceptance_criteria: [
            "Users can view memory grouped by rules, workflows, personas, decisions, and edge cases.",
            "Generated output remains candidate context until explicitly promoted.",
            "Memory items show source and status.",
          ],
          edge_cases: ["No product is selected", "Memory item conflicts with an existing decision"],
        },
        {
          id: "US-2",
          role: "founder",
          goal: "attach projects to a product later",
          benefit: "early work can start without forcing a perfect product hierarchy",
          acceptance_criteria: [
            "A project can exist without a product.",
            "A project can later be attached to a product from the project or product workspace.",
          ],
        },
      ],
    },
    {
      title: "Run Refinement",
      description: "A prompt-based loop for improving all or selected generated requirements.",
      stories: [
        {
          id: "US-3",
          role: "product manager",
          goal: "target specific epics or stories with a refinement prompt",
          benefit: "I can improve only the parts that need work",
          acceptance_criteria: [
            "No selection applies the prompt to the full artifact.",
            "Selected stories and epics are reflected in the composer target state.",
            "Review findings can prefill a fix prompt.",
          ],
        },
      ],
    },
  ],
};

export const DEMO_RESULT_PAYLOAD: RunResultsPayload & Record<string, any> = {
  claritySummary: {
    score: 82,
    riskLevel: "low",
    verdict: "Strong foundation. A few edge cases and ownership details should be clarified before engineering handoff.",
  },
  original: "Build product memory and refinement loops for generated requirements.",
  clarifyingQuestions: [
    "Which generated details should be promoted to product memory versus project context?",
    "Who can approve memory updates in a team workspace?",
    "Should refinement generate a new version automatically or wait for user approval?",
  ],
  requirements_document: DEMO_REQUIREMENTS_DOCUMENT,
  deep_evaluation: {
    overall_score: 82,
    verdict: "Mostly ready for engineering review",
    summary: "The requirements are structured and actionable. Remaining risks are around memory promotion rules and refinement versioning behaviour.",
    critique: [
      { title: "Memory promotion ownership is undefined", severity: "medium", detail: "The workflow says users can save useful details to memory, but does not define approval or ownership for teams." },
      { title: "Version behaviour needs a decision", severity: "medium", detail: "Refinement should clarify whether each prompt creates a version automatically or only after user acceptance." },
    ],
    engineering_questions: [
      "Does product memory retrieval happen before every run or only when enabled?",
      "How are conflicting context items resolved in the LLM prompt?",
    ],
    recommendations: [
      "Add a confirmed/candidate/superseded lifecycle to memory items.",
      "Make refinement prompts generate a proposed version rather than silently mutating the artifact.",
    ],
  },
  metadata: { processingTime: 2600, tokensUsed: 1850, model: "reqqy-demo" },
};

export const DEMO_RUN_RESULT = {
  ...DEMO_RUNS[0],
  result_payload: DEMO_RESULT_PAYLOAD,
  error_payload: null,
};
